import { getBankList } from '@/apis/bank';
import { getFeatures } from '@/apis/features';
import { getNationalityList } from '@/apis/nationality';
import { getPaymentType } from '@/apis/payment';
import { getVehicleMake, getVehicleModel, getVehicleVariant, getVehicleYear } from '@/apis/vehicle';

import { BankResponse } from '@/types/Bank';
import { FeatureConfig } from '@/types/Features';
import { NationalityResponse } from '@/types/Nationality';
import { PaymentType, PaymentTypeResponseWrapper } from '@/types/Payment';
import { UiState } from '@/types/UiState';
import { GetVehicleMakeResponse } from '@/types/Vehicle/VehicleMake';
import { GetVehicleModelResponse } from '@/types/Vehicle/VehicleModel';
import { GetVehicleVariantResponse } from '@/types/Vehicle/VehicleVariant';
import { GetVehicleYearResponse } from '@/types/Vehicle/VehicleYear';

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

// initial state uses class instance
const initialState: UiState = {
  language: `en`,
  bankList: [],
  loading: false,
  error: false,
  payModes: {
    data: [],
    loading: false,
    errors: [],
    succeeded: false,
    message: ``,
  },
  nationalityList: [],
  showSummary: false,
  vehicleMake: [],
  vehicleModel: [],
  vehicleYear: [],
  vehicleVariant: [],
  acknowledgeAffiliate: false,
};

export const uiStateSlice = createSlice({
  name: `uiState`,
  initialState,
  reducers: {
    resetUiState: () => {
      return initialState;
    },
    setShowPriceSummary: (state, action: PayloadAction<boolean>) => {
      state.showSummary = action.payload;
    },
    setAcknowledgeAffiliate: (state, action: PayloadAction<boolean>) => {
      state.acknowledgeAffiliate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBankList.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getBankList.fulfilled, (state, action) => {
        state.bankList = action.payload.data as BankResponse[];
        state.loading = false;
        state.error = false;
      })
      .addCase(getBankList.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
      });

    builder
      .addCase(getNationalityList.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getNationalityList.fulfilled, (state, action) => {
        state.nationalityList = action.payload.data as NationalityResponse[];
        state.loading = false;
        state.error = false;
      })
      .addCase(getNationalityList.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    // vehicle make api
    builder
      .addCase(getVehicleMake.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getVehicleMake.fulfilled, (state, action) => {
        state.vehicleMake = action.payload.data as GetVehicleMakeResponse[];
        state.loading = false;
        state.error = false;
      })
      .addCase(getVehicleMake.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    // vehicle model api
    builder
      .addCase(getVehicleModel.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getVehicleModel.fulfilled, (state, action) => {
        state.vehicleModel = action.payload.data as GetVehicleModelResponse[];
        state.loading = false;
        state.error = false;
      })
      .addCase(getVehicleModel.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    // vehicle year api
    builder
    .addCase(getVehicleYear.pending, (state) => {
      state.loading = true;
      state.error = false;
    })
    .addCase(getVehicleYear.fulfilled, (state, action) => {
      state.vehicleYear = action.payload.data as GetVehicleYearResponse[];
      state.loading = false;
      state.error = false;
    })
    .addCase(getVehicleYear.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });

    // vehicle variant api
    builder
    .addCase(getVehicleVariant.pending, (state) => {
      state.loading = true;
      state.error = false;
    })
    .addCase(getVehicleVariant.fulfilled, (state, action) => {
      state.vehicleVariant = action.payload.data as GetVehicleVariantResponse[];
      state.loading = false;
      state.error = false;
    })
    .addCase(getVehicleVariant.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });

    builder
      .addCase(getPaymentType.pending, (state) => {
        state.payModes.loading = true;
      })
      .addCase(getPaymentType.fulfilled, (state, action) => {
        state.payModes = action.payload;
        state.payModes.loading = false;
      })
      .addCase(getPaymentType.rejected, (state, action) => {
        state.payModes = action.payload as PaymentTypeResponseWrapper<PaymentType[]>;
        state.payModes.loading = false;
      });

    builder
      .addCase(getFeatures.pending, (state) => {
        state.error = false;
      })
      .addCase(getFeatures.fulfilled, (state, action) => {
        state.features = action.payload as FeatureConfig;
        state.error = false;
      })
      .addCase(getFeatures.rejected, (state) => {
        state.error = true;
      });
  },
});

export const { resetUiState, setShowPriceSummary, setAcknowledgeAffiliate } = uiStateSlice.actions;
export default uiStateSlice.reducer;
